import RealtimeAlertRubric from './realtime/RealtimeAlertRubric';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTabActivity } from '../contexts/TabActivityContext';
import { Check, Clock, Hash, FileText, ChevronDown, ChevronUp, SlidersHorizontal, ListTree, Filter, Maximize2, Minimize2 } from 'lucide-react';
import { Prompt } from '../services/api';
import { getEffectivePromptExecutionConfig, hasPromptSelectionFilter } from '../features/workflows/config';
import { formatEngineFieldLabel, getEngineConditionPreview, getEngineSegmentationLabel } from '../features/workflows/presentation';
import { extractConditionFieldsFromSchemas } from '../utils/conditionSearchFields';
import WorkflowFilterEditor from './WorkflowFilterEditor';
import {
  getPromptSchemaDisplayFieldsWithNesting,
  PromptSchemaDisplayFieldWithNesting
} from './prompt-schema/schemaFields';
import './PromptCard.css';

interface PromptCardProps {
  prompt: Prompt | null;
  isSelected: boolean;
  onClick: () => void;
  fallbackName?: string;
  fallbackDescription?: string;
  isDefault?: boolean;
  isCreating?: boolean;
  presentation?: 'workflow' | 'compact' | 'realtime-feed';
  showFieldDescriptions?: boolean;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  disabled?: boolean;
  showSelectionIndicator?: boolean;
  selectionLabel?: string;
  wrapperClassName?: string;
}

// Recursive component to render fields with nesting
const FieldItem: React.FC<{
  field: PromptSchemaDisplayFieldWithNesting;
  index: number;
  parentKey?: string;
  showDescription?: boolean;
  readableNames?: boolean;
}> = ({ field, index, parentKey = '', showDescription = false, readableNames = false }) => {
  const depthClass = field.depth >= 3 ? 'depth-3-plus' : `depth-${field.depth}`;
  const key = parentKey ? `${parentKey}-${field.name}-${index}` : `${field.name}-${index}`;

  return (
    <>
      <div className={`field-item ${depthClass} ${showDescription ? 'with-description' : ''}`}>
        <span className="field-copy">
          <span className="field-name" title={field.name}>{readableNames ? formatEngineFieldLabel(field.name) : field.name}</span>
          {showDescription && field.description && (
            <span className="field-description" title={field.description}>{field.description}</span>
          )}
        </span>
        <span className={`field-type ${field.styleCategory}`}>{readableNames ? field.typeLabel : field.compactTypeLabel}</span>
      </div>
      {field.nestedFields && field.nestedFields.map((nestedField, nestedIndex) => (
        <FieldItem
          key={`${key}-nested-${nestedIndex}`}
          field={nestedField}
          index={nestedIndex}
          parentKey={key}
          showDescription={showDescription}
          readableNames={readableNames}
        />
      ))}
    </>
  );
};

const FieldsList: React.FC<{
  fields: PromptSchemaDisplayFieldWithNesting[];
  showDescriptions?: boolean;
  readableNames?: boolean;
}> = ({ fields, showDescriptions = false, readableNames = false }) => (
  <>
    {fields.map((field, index) => (
      <FieldItem
        key={`${field.name}-${index}`}
        field={field}
        index={index}
        showDescription={showDescriptions}
        readableNames={readableNames}
      />
    ))}
  </>
);

type FieldsTab = 'segment' | 'video';

const PromptCard: React.FC<PromptCardProps> = ({
  prompt,
  isSelected,
  onClick,
  fallbackName = 'Extraction engine',
  fallbackDescription = 'This extraction engine is temporarily unavailable.',
  isDefault = false,
  isCreating = false,
  presentation = 'workflow',
  showFieldDescriptions = false,
  expanded,
  onExpandedChange,
  disabled = false,
  showSelectionIndicator = false,
  selectionLabel = 'Selected',
  wrapperClassName = '',
}) => {
  const { isActive, portalContainer } = useTabActivity();
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [activeTab, setActiveTab] = useState<FieldsTab>('segment');
  const [isHovered, setIsHovered] = useState(false);
  const hoverTimerRef = useRef<number | null>(null);
  const dismissHover = useCallback(() => {
    if (hoverTimerRef.current !== null) window.clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = null;
    setIsHovered(false);
  }, []);
  useEffect(() => () => {
    if (hoverTimerRef.current !== null) window.clearTimeout(hoverTimerRef.current);
  }, []);
  const [hoverGeometry, setHoverGeometry] = useState<React.CSSProperties>({});
  const workflowSlotRef = useRef<HTMLDivElement>(null);
  const workflowCardRef = useRef<HTMLElement>(null);
  const workflowPinned = expanded ?? isExpanded;
  const fieldsListRef = useRef<HTMLDivElement>(null);
  const isFeedPresentation = presentation === 'realtime-feed';
  const executionConfig = prompt && presentation !== 'compact'
    ? getEffectivePromptExecutionConfig(prompt)
    : null;

  const exploreOnHover = (event: React.PointerEvent) => {
    if (isFeedPresentation || disabled || workflowPinned || isHovered || hoverTimerRef.current !== null || event.pointerType !== 'mouse'
      || !window.matchMedia('(hover: hover)').matches) return;
    const slot = workflowSlotRef.current;
    if (!slot) return;
    const rect = slot.getBoundingClientRect();
    const leftBound = 8;
    const rightBound = window.innerWidth - 8;
    const topBound = 8;
    const bottomBound = window.innerHeight - 8;
    const width = Math.min(Math.max(540, rect.width + 140), rightBound - leftBound);
    const height = Math.min(600, bottomBound - topBound);
    const left = Math.max(leftBound, Math.min(rect.left - (width - rect.width) / 2, rightBound - width));
    const top = Math.max(topBound, Math.min(rect.top - 12, bottomBound - height));
    setHoverGeometry({ width, height, left, top });
    hoverTimerRef.current = window.setTimeout(() => {
      hoverTimerRef.current = null;
      setIsHovered(true);
    }, 180);
  };

  useEffect(() => {
    if (!isActive || disabled || workflowPinned) dismissHover();
  }, [isActive, disabled, dismissHover, workflowPinned]);

  useEffect(() => {
    if (!isActive || !isHovered || workflowPinned) return;
    const dismiss = (event: KeyboardEvent) => {
      if (event.key === 'Escape') dismissHover();
    };
    const dismissOnPageScroll = (event: Event) => {
      if (!(event.target instanceof Node) || !workflowCardRef.current?.contains(event.target)) dismissHover();
    };
    // The preview leaves the page's scrolling DOM ancestors when portalled.
    // Keep native scrolling inside it, then chain wheel movement to its page.
    const forwardPageWheel = (event: WheelEvent) => {
      const horizontal = event.shiftKey || Math.abs(event.deltaX) > Math.abs(event.deltaY);
      const movement = horizontal ? event.deltaX || event.deltaY : event.deltaY;
      if (event.ctrlKey || !movement) return;
      const card = workflowCardRef.current;
      let element = event.target instanceof Element ? event.target : null;
      const canScroll = (candidate: HTMLElement) => {
        const style = window.getComputedStyle(candidate);
        const overflow = horizontal ? style.overflowX : style.overflowY;
        const offset = horizontal ? candidate.scrollLeft : candidate.scrollTop;
        const viewport = horizontal ? candidate.clientWidth : candidate.clientHeight;
        const extent = horizontal ? candidate.scrollWidth : candidate.scrollHeight;
        return /^(auto|scroll)$/.test(overflow)
          && (movement > 0 ? offset + viewport < extent : offset > 0);
      };
      while (element instanceof HTMLElement && card?.contains(element)) {
        if (canScroll(element)) return;
        element = element.parentElement;
      }
      let parent = workflowSlotRef.current?.parentElement;
      while (parent) {
        if (canScroll(parent)) {
          const viewport = horizontal ? parent.clientWidth : parent.clientHeight;
          const delta = movement * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? viewport : 1);
          if (horizontal) parent.scrollLeft += delta;
          else parent.scrollTop += delta;
          event.preventDefault();
          return;
        }
        parent = parent.parentElement;
      }
    };
    const card = workflowCardRef.current;
    card?.addEventListener('wheel', forwardPageWheel, { passive: false });
    window.addEventListener('keydown', dismiss);
    window.addEventListener('scroll', dismissOnPageScroll, true);
    window.addEventListener('resize', dismissHover);
    return () => {
      card?.removeEventListener('wheel', forwardPageWheel);
      window.removeEventListener('keydown', dismiss);
      window.removeEventListener('scroll', dismissOnPageScroll, true);
      window.removeEventListener('resize', dismissHover);
    };
  }, [isActive, dismissHover, isHovered, workflowPinned]);

  // Get fields for both segment and video level
  const segmentFields = prompt
    ? getPromptSchemaDisplayFieldsWithNesting(prompt.json_schema)
    : [];
  const hasVideoLevel = !!(prompt?.video_level?.json_schema);
  const videoFields = hasVideoLevel
    ? getPromptSchemaDisplayFieldsWithNesting(prompt!.video_level!.json_schema)
    : [];

  // Count total fields including nested fields recursively
  const countFields = (fields: PromptSchemaDisplayFieldWithNesting[]): number => {
    return fields.reduce((count, field) => {
      const nestedCount = field.nestedFields ? countFields(field.nestedFields) : 0;
      return count + 1 + nestedCount;
    }, 0);
  };

  // Reset to segment tab if video level is removed
  useEffect(() => {
    if (!hasVideoLevel && activeTab === 'video') {
      setActiveTab('segment');
    }
  }, [hasVideoLevel, activeTab]);

  // Get active fields based on selected tab
  const activeFields = activeTab === 'segment' ? segmentFields : videoFields;
  const totalFields = countFields(activeFields);

  // Check if fields list has overflow
  useEffect(() => {
    if (presentation !== 'compact') return;
    const checkOverflow = () => {
      if (fieldsListRef.current) {
        const hasScroll = fieldsListRef.current.scrollHeight > fieldsListRef.current.clientHeight;
        setHasOverflow(hasScroll);
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [prompt, activeTab, presentation]);

  const handleTabClick = (e: React.MouseEvent, tab: FieldsTab) => {
    e.stopPropagation(); // Prevent card selection
    setActiveTab(tab);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card selection
    setIsExpanded(!isExpanded);
  };

  const handleCardClick = () => {
    if (!disabled && prompt) {
      dismissHover();
      onClick();
    }
  };

  const handleCardKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled || !prompt || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    onClick();
  };

  const isCompact = presentation === 'compact';
  const hasFilter = hasPromptSelectionFilter(prompt?.selection_filter);
  const filterGroups = (['segment', 'video'] as const).flatMap((level) => {
    const group = prompt?.selection_filter?.[level];
    return group?.conditions.length ? [{ level, group }] : [];
  });
  const isUnavailable = disabled || !prompt;
  const footerLabel = isUnavailable
    ? 'Unavailable'
    : isCreating
      ? 'Draft'
      : formatDate(prompt!.created_at);

  if (presentation !== 'compact' && prompt && executionConfig) {
    const workflowExpanded = !isFeedPresentation && (workflowPinned || isHovered);
    const stageHeading = (title: string, icon: React.ReactNode) => (
      <div className="prompt-workflow-stage-heading">
        {icon}
        <span className="prompt-workflow-stage-title">{title}</span>
      </div>
    );
    const workflowCard = (
        <article
          ref={workflowCardRef}
          className={`prompt-workflow-card${prompt.is_realtime ? ' is-realtime' : ''}${isSelected ? ' selected' : ''}${isCreating ? ' creating' : ''}${workflowExpanded ? ' is-expanded' : ''}${isHovered && !workflowPinned ? ' is-exploring' : ''}${disabled ? ' disabled' : ''}`}
          style={isHovered && !workflowPinned ? hoverGeometry : undefined}
          aria-label={prompt.name}
          onClick={handleCardClick}
          onPointerLeave={dismissHover}
          onWheel={() => { if (!isHovered) dismissHover(); }}
        >
          <header className="prompt-workflow-header">
            <div className="prompt-workflow-heading-copy">
              <h4>
                <button
                  type="button"
                  className="prompt-workflow-select"
                  aria-pressed={isSelected}
                  disabled={disabled}
                  onClick={(event) => { event.stopPropagation(); handleCardClick(); }}
                >
                  {isSelected && <Check size={14} aria-hidden="true" />}
                  <span>{prompt.name}</span>
                </button>
                {isDefault && <span className="prompt-workflow-badge">Default</span>}
                {isCreating && <span className="prompt-workflow-badge">Draft</span>}
              </h4>
              <p>{prompt.description}</p>
            </div>
            <div className="prompt-workflow-header-actions">
              {!isFeedPresentation && <button
                type="button"
                className="prompt-workflow-resize"
                disabled={disabled}
                aria-label={workflowPinned ? `Collapse ${prompt.name}` : `Keep ${prompt.name} expanded`}
                title={workflowPinned ? 'Collapse card' : isHovered ? 'Keep expanded' : 'Explore engine'}
                aria-expanded={workflowExpanded}
                aria-controls={`engine-stages-${prompt.prompt_id}`}
                onClick={(event) => {
                  event.stopPropagation();
                  dismissHover();
                  if (onExpandedChange) onExpandedChange(!workflowPinned);
                  else setIsExpanded(!workflowPinned);
                }}
              >
                {workflowPinned ? <Minimize2 size={14} aria-hidden="true" /> : <Maximize2 size={14} aria-hidden="true" />}
              </button>}
            </div>
          </header>

          <div className="prompt-workflow-stages" id={`engine-stages-${prompt.prompt_id}`}>
            <section className="prompt-workflow-stage prompt-workflow-extraction" aria-label="Extracted fields">
              {stageHeading('Extraction fields', <ListTree size={14} aria-hidden="true" />)}
              <div className={`prompt-workflow-field-groups${hasVideoLevel ? ' has-video' : ''}`}>
                <div className="prompt-workflow-field-group">
                  <h6>{prompt.is_realtime ? 'Observations' : 'Segments'} <span className="prompt-workflow-chip">{countFields(segmentFields)}</span></h6>
                  <div className="prompt-workflow-fields">
                    {segmentFields.length > 0 ? <FieldsList fields={segmentFields} showDescriptions readableNames /> : <p>No fields defined</p>}
                  </div>
                </div>
                {hasVideoLevel && (
                  <div className="prompt-workflow-field-group">
                    <h6>Whole video <span className="prompt-workflow-chip">{countFields(videoFields)}</span></h6>
                    <div className="prompt-workflow-fields"><FieldsList fields={videoFields} showDescriptions readableNames /></div>
                  </div>
                )}
              </div>
            </section>

            {(workflowExpanded || prompt.is_realtime) && <div className="prompt-workflow-supporting-details">
              {!prompt.is_realtime && workflowExpanded && <section className="prompt-workflow-stage prompt-workflow-processing" aria-label="Processing settings">
                {stageHeading('Processing', <SlidersHorizontal size={14} aria-hidden="true" />)}
                <div className="prompt-workflow-summary">
                  <span className="prompt-workflow-chip">{getEngineSegmentationLabel(executionConfig)}</span>
                  <span className="prompt-workflow-chip">Visual search {executionConfig.enable_image_embedding ? 'on' : 'off'}</span>
                </div>
              </section>}

              {prompt.is_realtime && <section className="prompt-workflow-stage" aria-label="Alert levels">
                {stageHeading('Alerts', <Filter size={14} aria-hidden="true" />)}
                <RealtimeAlertRubric value={prompt.realtime_config} compact />
              </section>}

              {workflowExpanded && <section className="prompt-workflow-stage prompt-workflow-selection" aria-label="Selection filters">
                {stageHeading('Filters', <Filter size={14} aria-hidden="true" />)}
                {hasFilter ? (
                  <WorkflowFilterEditor
                    value={prompt.selection_filter || null}
                    segmentFields={extractConditionFieldsFromSchemas([prompt.json_schema])}
                    videoFields={hasVideoLevel ? extractConditionFieldsFromSchemas([prompt.video_level!.json_schema]) : []}
                    editable={false}
                    onChange={() => undefined}
                  />
                ) : <div className="prompt-workflow-summary"><span className="prompt-workflow-chip">All extracted results</span></div>}
              </section>}
            </div>}
          </div>
        </article>
    );
    return (
      <div
        ref={workflowSlotRef}
        className={`prompt-workflow-slot${isFeedPresentation ? ' prompt-workflow-slot--realtime-feed' : ''}${workflowPinned && !isFeedPresentation ? ' is-pinned' : ''}`}
        onPointerEnter={exploreOnHover}
        onPointerLeave={(event) => {
          if (!(event.relatedTarget instanceof Node) || !workflowCardRef.current?.contains(event.relatedTarget)) dismissHover();
        }}
      >
        {isHovered && !workflowPinned ? createPortal(workflowCard, portalContainer || document.body) : workflowCard}
      </div>
    );
  }

  return (
    <div className={`prompt-card-wrapper ${isCompact ? 'prompt-card-wrapper--compact' : ''} ${wrapperClassName}`.trim()}>
      <div
        className={`prompt-card ${isSelected ? 'selected' : ''} ${isCreating ? 'creating' : ''} ${isDefault ? 'default' : ''} ${isExpanded ? 'expanded' : ''} ${isCompact ? 'prompt-card--compact' : ''} ${isUnavailable ? 'disabled' : ''}`}
        onClick={handleCardClick}
        onKeyDown={handleCardKeyDown}
        role="button"
        tabIndex={isUnavailable ? -1 : 0}
        aria-pressed={isSelected}
        aria-disabled={isUnavailable || undefined}
      >
      {/* Top color bar */}
      <div className="prompt-card-colorbar" />

      {/* Header */}
      <div className="prompt-card-header">
        <div className="prompt-card-title">
          <span className="prompt-name">{prompt?.name || fallbackName}</span>
        </div>
        {showSelectionIndicator && isSelected && (
          <span className="prompt-card-selection-indicator">
            <Check size={12} aria-hidden="true" />
            {selectionLabel}
          </span>
        )}
      </div>

      {/* Description */}
      <div className="prompt-card-description">
        {prompt?.description || fallbackDescription}
      </div>

      {/* Fields Section */}
      <div className="prompt-card-fields-section">
        <div className="fields-section-header">
          <div className="fields-count">
            <Hash size={14} />
            <span>{totalFields} fields</span>
          </div>
          {hasVideoLevel && (
            <div className="fields-tab-switch">
              <button
                className={`fields-tab ${activeTab === 'segment' ? 'active' : ''}`}
                onClick={(e) => handleTabClick(e, 'segment')}
              >
                Segment
              </button>
              <button
                className={`fields-tab ${activeTab === 'video' ? 'active' : ''}`}
                onClick={(e) => handleTabClick(e, 'video')}
              >
                Video
              </button>
            </div>
          )}
        </div>

        {/* Fields list with internal scrollbar */}
        <div className="fields-list" ref={fieldsListRef}>
          {activeFields.length === 0 ? (
            <div className="no-fields">No fields defined</div>
          ) : (
            <FieldsList fields={activeFields} showDescriptions={showFieldDescriptions} />
          )}
        </div>

        {/* Show more/less button - only visible on hover when there's overflow */}
        {hasOverflow && (
          <button
            className="prompt-card-expand-btn"
            onClick={handleExpandClick}
            aria-label={isExpanded ? "Show less" : "Show more"}
          >
            {isExpanded ? (
              <>
                <ChevronUp size={14} />
                <span>Show less</span>
              </>
            ) : (
              <>
                <ChevronDown size={14} />
                <span>Show more</span>
              </>
            )}
          </button>
        )}
      </div>

      {prompt?.is_realtime && <RealtimeAlertRubric value={prompt.realtime_config} compact />}

      {prompt && isExpanded && (
        <section className="prompt-card-filter-summary" aria-label="Selection filters">
          <strong><Filter size={12} aria-hidden="true" />{hasFilter ? 'Matches' : 'All extracted results'}</strong>
          {filterGroups.map(({ level, group }, index) => (
            <p key={level}>
              {index > 0 && <b>{prompt.selection_filter?.level_logic.toUpperCase()} </b>}
              {level === 'video' ? 'Whole video' : 'Segments'}: {group.conditions.map(getEngineConditionPreview).join(` ${group.logic.toUpperCase()} `)}
            </p>
          ))}
        </section>
      )}

      {/* Footer */}
      <div className="prompt-card-footer">
        <div className="prompt-card-meta">
          <Clock size={12} />
          <span>{footerLabel}</span>
        </div>
        <FileText size={14} className="card-icon" />
      </div>
      </div>
    </div>
  );
};

export default PromptCard;
